// Liveness watchdog for thread-detail subscriptions.
//
// Symptom this guards against: the chat timeline freezes on a stale "Working…"
// spinner after a websocket disconnect. A turn can finish while the socket is
// briefly down; the terminal event (`task_complete`, which flips the session
// from "running" to "idle") arrives at the dead socket and is lost, and the
// transport's resubscribe does not always deliver a fresh snapshot. The UI then
// reads the last-seen `session.status === "running"` forever — until a manual
// reload forces a fresh `subscribeThread` snapshot.
//
// The codebase has all the machinery to self-heal (every `subscribeThread`
// re-emits a snapshot that carries turn state), yet at least one disconnect
// class leaves it stuck and is not reproducible offline. Rather than guess the
// exact transport edge, the watchdog enforces the invariant directly: if the UI
// believes a turn is running but the stream has gone silent past a threshold,
// force the client back to authoritative state. The escalation from a cheap
// resubscribe to a full reconnect ensures a half-dead socket cannot keep
// starving the subscription.

export type ThreadDetailLivenessAction = "none" | "resubscribe" | "reconnect";

export interface ThreadDetailLivenessInput {
  /** The UI currently shows this thread as working (a "running" spinner). */
  readonly isWorking: boolean;
  /** Milliseconds since the subscription last received any stream item. */
  readonly msSinceLastEvent: number;
  /** How long a working thread may go silent before we treat it as stalled. */
  readonly stallThresholdMs: number;
  /** Recovery actions already taken for the current stall (reset on activity). */
  readonly recoveryAttempts: number;
  /** Cheap resubscribes to try before escalating to a full reconnect. */
  readonly maxResubscribeAttempts: number;
  /**
   * Hard ceiling on recovery actions for one stall. Beyond it we give up rather
   * than reconnect forever: if the snapshot still reports the turn running after
   * this many tries the turn is almost certainly genuinely running (so the
   * spinner is correct), and a manual reload remains the escape hatch. Bounds a
   * pathological reconnect storm.
   */
  readonly maxTotalAttempts: number;
}

/**
 * Decide whether a thread-detail subscription that the UI believes is working
 * has gone silent long enough to need recovery, and how hard to push.
 *
 * - not working, or still receiving events recently => "none"
 * - working + stalled, resubscribe budget remaining => "resubscribe"
 * - working + stalled, resubscribe budget exhausted => "reconnect"
 * - working + stalled, total budget exhausted => "none" (give up; do not storm)
 *
 * A resubscribe that delivers a fresh snapshot resets recoveryAttempts via the
 * stream handler, so legitimately-running-but-quiet turns (e.g. a long tool
 * call) keep getting cheap silent resubscribes and never escalate. Escalation
 * to a reconnect only happens when resubscribes deliver nothing — a truly dead
 * socket — which is the only case worth a Sentry signal.
 */
export function deriveThreadDetailLivenessAction(
  input: ThreadDetailLivenessInput,
): ThreadDetailLivenessAction {
  if (!input.isWorking) {
    return "none";
  }
  if (input.msSinceLastEvent < input.stallThresholdMs) {
    return "none";
  }
  if (input.recoveryAttempts < input.maxResubscribeAttempts) {
    return "resubscribe";
  }
  if (input.recoveryAttempts < input.maxTotalAttempts) {
    return "reconnect";
  }
  return "none";
}

// A working turn streams events frequently (token counts, tool calls). 30s of
// total silence on a thread the UI thinks is running is well past any normal
// gap and signals a dropped subscription.
export const THREAD_DETAIL_STALL_THRESHOLD_MS = 30_000;

// Try two cheap resubscribes before the heavier full-session reconnect.
export const THREAD_DETAIL_MAX_RESUBSCRIBE_ATTEMPTS = 2;

// Stop after two reconnect escalations (4 total actions). Past this a stuck
// "running" almost certainly reflects a real running turn, not a dropped
// subscription, so we stop churning the websocket.
export const THREAD_DETAIL_MAX_TOTAL_ATTEMPTS = 4;

// How often the watchdog scans active subscriptions. Comfortably below the
// stall threshold so a stall is caught within roughly one threshold window.
export const THREAD_DETAIL_WATCHDOG_INTERVAL_MS = 10_000;
